<template>
  <div class="bg-white">
    <!-- Fixed Navigation with Backdrop Blur -->
    <nav
      class="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200"
      role="navigation"
      aria-label="Main navigation"
    >
      <div class="mx-auto px-10">
        <div class="flex justify-between items-center h-16">
          <!-- Logo/Brand Section -->
          <router-link to="/" class="flex items-center gap-2">
            <img
              src="../../assets/icon/logo-CAqQhPFA.png"
              alt="OptimaAI Creator"
              class="w-8 h-8"
            />
            <span class="text-lg md:text-xl font-bold text-gray-900">
              OptimaAI Creator
            </span>
          </router-link>

          <!-- Desktop Menu - Hidden on mobile -->
          <div class="hidden md:flex items-center gap-8"></div>

          <!-- Desktop Buttons - Hidden on mobile -->
          <div class="hidden md:flex items-center gap-4">
            <!-- Contact Us Button -->
            <a href="#contact">
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(207,90%,60%)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-gray-100 hover:text-gray-900 h-10 px-4 py-2 text-gray-900"
              >
                Contact Us
              </button>
            </a>

            <!-- Login/Join Button -->
            <button
              @click="handleLoginJoin"
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(207,90%,60%)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2 bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,85%,48%)]"
              aria-label="Login or Join OptimaAI Creator"
            >
              Login/Join
            </button>
          </div>

          <!-- Mobile Hamburger Button - Visible on mobile only -->
          <button
            @click="toggleMobileMenu"
            class="md:hidden p-2"
            aria-label="Toggle menu"
            :aria-expanded="mobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="w-6 h-6 text-gray-900"
            >
              <line x1="4" x2="20" y1="12" y2="12"></line>
              <line x1="4" x2="20" y1="6" y2="6"></line>
              <line x1="4" x2="20" y1="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Backdrop - Visible when menu is open -->
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
      @click="closeMobileMenu"
      aria-hidden="true"
    ></div>

    <!-- Mobile Menu Slide-in Panel -->
    <div
      :class="[
        'fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden',
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      ]"
    >
      <!-- Menu Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 class="text-xl font-bold text-gray-900">Menu</h2>
        <button
          @click="closeMobileMenu"
          class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          aria-label="Close menu"
        >
          <svg
            class="w-6 h-6 text-gray-900"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Menu Content -->
      <div class="flex flex-col p-6">
        <!-- Contact Us -->
        <a
          href="#contact"
          @click="closeMobileMenu"
          class="text-left py-4 text-gray-700 font-medium text-lg border-b border-gray-200 hover:text-[hsl(207,90%,54%)] transition-colors duration-200"
        >
          Contact Us
        </a>

        <!-- Login/Join Button -->
        <button
          @click="handleLoginJoin"
          class="mt-6 bg-[hsl(207,90%,54%)] text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-semibold hover:scale-105 active:scale-95"
          aria-label="Login or Join OptimaAI Creator"
        >
          Login/Join
        </button>
      </div>
    </div>

    <section class="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 pb-4 bg-gradient-to-b from-gray-light to-background">
      <div class="container mx-auto max-w-[1200px] px-6 relative z-10">
        <div class="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <div class="space-y-8 animate-fade-in">
            <div class="inline-block px-4 py-1 bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,54%)] rounded-full text-sm font-semibold mb-4">
              New
            </div>
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
              The Only Platform Only Fans Creators Need to
              <span class="text-[hsl(207,90%,54%)]"> Grow Faster & Earn More</span>
            </h1>
            <p class="text-lg text-gray-600 max-w-xl">
              OptimaAI Creator is your all-in-one AI assistant that helps content creators manage content, chat smarter, and boost income — across all your platforms at once.
            </p>
            <div class="inline-block px-4 py-2 bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,54%)] rounded-lg font-semibold">
              All 10 Modules for - $149 per month
            </div>
            <div class="inline-block px-4 py-2 bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,54%)] rounded-lg font-semibold">
              2-Week FREE TRIAL • No Credit Card Required
            </div>
            <div class="flex flex-wrap gap-4">
              <button
                @click="goToSignup"
                class="inline-flex text-white items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(207,90%,60%)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 rounded-md text-base px-8 bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,85%,48%)] shadow-lg"
              >
                Start Your Free Trial
              </button>
              <button
                @click="watchVideo('promo')"
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(207,90%,60%)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-white hover:bg-gray-100 hover:text-gray-900 h-11 rounded-md px-8 text-base"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="w-4 h-4 mr-2"
                >
                  <polygon points="6 3 20 12 6 21 6 3"></polygon>
                </svg>
                Watch Video
              </button>
            </div>
          </div>
          <div class="flex justify-center">
            <img
              src="../../assets/image/creator-1-DTYBmnhQ.png"
              alt="OptimaAI Creator Dashboard"
              class="w-full max-w-[350px] h-auto rounded-3xl shadow-2xl animate-float"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- WHAT WE DO SECTION -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div class="max-w-[1600px] mx-auto">
        <div class="text-center mb-12 bg-primary/60 px-10 text-white py-2 rounded-full w-fit mx-auto">
          Tech
        </div>
        <!-- Top Section - Headline and CTA -->
        <div class="mb-12 text-center">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            One Dashboard. <br /> All Your Platforms. <br /> Zero Chaos.
          </h2>
          <p class="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            Manage OnlyFans, ManyVids, Fansly, and more — all from one AI-powered dashboard. Simplify your workflow and maximize earnings with intelligent automation.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button class="bg-primary inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
              All 10 Modules for - $149 per month
            </button>
            <button
              @click="goToSignup"
              class="bg-primary inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold"
            >
              Start Free Trial • No Credit Card Required
            </button>
          </div>
        </div>

        <!-- Dashboard Container -->
        <div class="w-full max-w-5xl mx-auto">
          <img
            src="../../assets/image/dashboard-new-DyKVhDaR.png"
            alt="Dashboard"
            class="w-full h-auto rounded-2xl shadow-2xl border border-border hover:scale-[1.02] transition-transform duration-300"
          />
        </div>
        <!-- Feature Cards -->
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto my-10">
          <!-- Card 1: Save Hours Every Week -->
          <div class="bg-card rounded-2xl p-6 shadow-card hover:shadow-xl transition-all border border-border hover:border-primary/50">
            <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-primary">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <h3 class="font-semibold text-foreground mb-2">Save Hours Every Week</h3>
            <p class="text-sm text-muted-foreground">AI-driven automation handles repetitive tasks so you can focus on creating content.</p>
          </div>

          <!-- Card 2: Increase Revenue up to 40% -->
          <div class="bg-card rounded-2xl p-6 shadow-card hover:shadow-xl transition-all border border-border hover:border-primary/50">
            <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-primary">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                <polyline points="16 7 22 7 22 13"></polyline>
              </svg>
            </div>
            <h3 class="font-semibold text-foreground mb-2">Increase Revenue up to 40%</h3>
            <p class="text-sm text-muted-foreground">Smart insights help you optimize pricing, posting times, and engagement strategies.</p>
          </div>

          <!-- Card 3: Unified Analytics -->
          <div class="bg-card rounded-2xl p-6 shadow-card hover:shadow-xl transition-all border border-border hover:border-primary/50">
            <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-primary">
                <path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
                <path d="M18 17V9"></path>
                <path d="M13 17V5"></path>
                <path d="M8 17v-3"></path>
              </svg>
            </div>
            <h3 class="font-semibold text-foreground mb-2">Unified Analytics</h3>
            <p class="text-sm text-muted-foreground">Track all your metrics from multiple platforms in one place, no more tab switching.</p>
          </div>

          <!-- Card 4: Expert Support 24/7 -->
          <div class="bg-card rounded-2xl p-6 shadow-card hover:shadow-xl transition-all border border-border hover:border-primary/50">
            <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-primary">
                <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
              </svg>
            </div>
            <h3 class="font-semibold text-foreground mb-2">Expert Support 24/7</h3>
            <p class="text-sm text-muted-foreground">Get help from all-star experts in your niche to maximize your earning potential.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ABOUT US SECTION -->
    <section class="py-8 bg-primary/10">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="text-center mb-12 space-y-4">
          <div class="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-2">
            SELLS
          </div>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Why Creators Love It
          </h2>
          <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
            All 10 Modules for - $149 per month
          </div>
          <p class="text-muted-foreground max-w-xl mx-auto">
            More Platforms, More Money — Without More Work
          </p>
        </div>
        <div class="max-w-4xl mx-auto mb-10">
          <div class="relative overflow-hidden bg-gradient-to-br from-primary/10 via-blue-dark/5 to-primary/10 rounded-3xl border-2 border-primary/20 shadow-xl hover:shadow-2xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div class="absolute bottom-0 left-0 w-64 h-64 bg-blue-dark/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            <div class="relative z-10 p-8 md:p-12 text-center space-y-6">
              <div class="flex justify-center gap-3 flex-wrap">
                <span class="inline-block px-4 py-2 bg-primary text-white rounded-full text-xs font-bold tracking-wide shadow-lg">
                  WORLD-FIRST INNOVATION
                </span>
                <span class="inline-block px-4 py-2 bg-amber-500 text-white rounded-full text-xs font-bold tracking-wide shadow-lg">
                  PATENT PENDING
                </span>
              </div>
              <h3 class="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                The Amazing Help Button
              </h3>
              <p class="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Why is it called The Amazing Help button? Because it is truely amazing. Watch the video.
              </p>
              <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
                All 10 Modules for - $149 per month
              </div>
              <div class="pt-4">
                <button
                  @click="watchVideo('help')"
                  class="group inline-flex items-center gap-3 bg-primary hover:bg-blue-dark text-white px-10 py-5 rounded-2xl font-bold text-xl shadow-2xl hover:shadow-primary/50 hover:scale-105 transition-all duration-300"
                >
                  <div class="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 fill-white">
                      <polygon points="6 3 20 12 6 21 6 3"></polygon>
                    </svg>
                  </div>
                  Watch Video
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-2xl mx-auto space-y-6">
          <div class="bg-white flex items-start gap-4 bg-card rounded-2xl p-6 shadow-card border border-border hover:border-primary/50 transition-colors">
            <div class="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-white">
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>
            <p class="text-foreground text-lg">
              AI that understands your content style and automates engagement authentically
            </p>
          </div>
          <div class="bg-white flex items-start gap-4 bg-card rounded-2xl p-6 shadow-card border border-border hover:border-primary/50 transition-colors">
            <div class="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-white">
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>
            <p class="text-foreground text-lg">
              Advanced analytics that help you reach the top with more views and better rankings
            </p>
          </div>
          <div class="bg-white flex items-start gap-4 bg-card rounded-2xl p-6 shadow-card border border-border hover:border-primary/50 transition-colors">
            <div class="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-white">
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>
            <p class="text-foreground text-lg">
              Comprehensive reporting and tracking to optimize costs and maximize profits
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- EVERYTHING YOU NEED TO SCALE SECTION -->
    <section class="py-8 bg-gradient-to-b from-grayLight to-white" id="how-it-works">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="text-center mb-16 space-y-4">
          <div class="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-2">
            Features
          </div>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            Everything You Need to Scale
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto mb-4">
            From setup to success, OptimaAI Creator gives you the tools to grow your creator business effortlessly.
          </p>
          <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
            All 10 Modules for - $149 per month
          </div>
          <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
            Try All Features Free for 2 Weeks
          </div>
        </div>
        <div class="space-y-12 sm:space-y-20 md:space-y-32 max-w-7xl mx-auto">
          <div class="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div class="flex justify-center">
              <div class="relative w-full">
                <img
                  src="../../assets/image/dashboard-analytics-BG4zrvoL.png"
                  alt="Advanced Analytics & Insights"
                  class="w-full max-w-full sm:max-w-[500px] lg:max-w-[600px] h-auto rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl border border-gray-200 hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
            <div class="space-y-4 sm:space-y-6">
              <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-blue-700 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 sm:w-7 sm:h-7 text-white">
                  <path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
                  <path d="M18 17V9"></path>
                  <path d="M13 17V5"></path>
                  <path d="M8 17v-3"></path>
                </svg>
              </div>
              <h3 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Advanced Analytics & Insights
              </h3>
              <p class="text-base sm:text-lg text-gray-600 leading-relaxed">
                Track your performance across all platforms with real-time analytics. Monitor revenue breakdown, fan segments, and campaign trends to make data-driven decisions.
              </p>
              <div class="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4 pt-4">
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">Real-time</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Revenue Tracking</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">Deep</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Fan Insights</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">Optimized</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Campaigns</div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center lg:flex-row-reverse">
            <div class="flex justify-center lg:order-2">
              <div class="relative w-full">
                <img
                  src="../../assets/image/security-dashboard-CtmgrsCV.png"
                  alt="Bank-Level Security & Privacy"
                  class="w-full max-w-full sm:max-w-[500px] lg:max-w-[600px] h-auto rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl border border-gray-200 hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
            <div class="space-y-4 sm:space-y-6 lg:order-1">
              <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-blue-700 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 sm:w-7 sm:h-7 text-white">
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                </svg>
              </div>
              <h3 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Bank-Level Security & Privacy
              </h3>
              <p class="text-base sm:text-lg text-gray-600 leading-relaxed">
                Your content and data are protected with 2FA authentication, end-to-end encryption, watermarking, and fingerprinting. Stay compliant with 95% GDPR & CCPA standards.
              </p>
              <div class="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4 pt-4">
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">2FA</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Protection</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">E2E</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Encryption</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">95%</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Compliance</div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div class="flex justify-center">
              <div class="relative w-full">
                <img
                  src="../../assets/image/connect-accounts-DRZpf4k7.png"
                  alt="Connect All Your Accounts"
                  class="w-full max-w-full sm:max-w-[500px] lg:max-w-[600px] h-auto rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl border border-gray-200 hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
            <div class="space-y-4 sm:space-y-6">
              <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-blue-700 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 sm:w-7 sm:h-7 text-white">
                  <path d="M9 17H7A5 5 0 0 1 7 7h2"></path>
                  <path d="M15 7h2a5 5 0 1 1 0 10h-2"></path>
                  <line x1="8" x2="16" y1="12" y2="12"></line>
                </svg>
              </div>
              <h3 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Connect All Your Accounts
              </h3>
              <p class="text-base sm:text-lg text-gray-600 leading-relaxed">
                Seamlessly link OnlyFans, ManyVids, Fansly, and social platforms like Instagram and TikTok. Manage everything from one unified dashboard.
              </p>
              <div class="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4 pt-4">
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">6+</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Platforms</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">1-Click</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Setup</div>
                </div>
                <div class="text-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200">
                  <div class="text-xl sm:text-2xl font-bold text-primary mb-1">Instant</div>
                  <div class="text-[10px] sm:text-xs text-gray-600 leading-tight">Sync</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ADVANCED SERVICES SECTION -->
    <section class="py-8 bg-white" id="multi-platform">
      <div class="container mx-auto max-w-[1200px] px-6">
        <!-- Top Section - Feature Tag, Heading, and Subheading -->
        <div class="text-center mb-12 space-y-4">
          <!-- Feature Tag -->
          <div class="inline-block px-4 py-1 bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,54%)] rounded-full text-sm font-semibold mb-2">
            FEATURE
          </div>

          <!-- Main Heading -->
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            One Login Three Platforms<br />Zero Extra Work.
          </h2>

          <!-- Subheading -->
          <p class="text-gray-600 max-w-2xl mx-auto">
            Join thousands of creators using OptimaAI to grow their business on OnlyFans, ManyVids & Fansly — with less effort and signup earnings.
          </p>
        </div>

        <!-- Main Content - Two Column Layout -->
        <div class="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <!-- Left Column - Benefits -->
          <div class="space-y-8">
            <!-- Section Heading -->
            <h3 class="text-2xl font-bold text-gray-900">
              Save Time. Make More Work. Less.
            </h3>

            <!-- Benefit 1 -->
            <div class="flex gap-4">
              <div class="w-12 h-12 rounded-2xl bg-[hsl(207,90%,54%)]/10 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-[hsl(207,90%,54%)]">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div>
                <h4 class="font-semibold text-gray-900 mb-2">
                  Everything Simplified. Nothing Complicated.
                </h4>
                <p class="text-gray-600 text-sm">
                  Stop wasting time switching accounts and tabs. Just login once and manage everything with one click.
                </p>
              </div>
            </div>

            <!-- Benefit 2 -->
            <div class="flex gap-4">
              <div class="w-12 h-12 rounded-2xl bg-[hsl(207,90%,54%)]/10 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-[hsl(207,90%,54%)]">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div>
                <h4 class="font-semibold text-gray-900 mb-2">
                  One Platform. All Solutions. Zero Stress.
                </h4>
                <p class="text-gray-600 text-sm">
                  All platforms, analytics, or paid stats across all platforms in a neat, unified dashboard so you always have clarity.
                </p>
              </div>
            </div>
          </div>

          <!-- Right Column - Animated Image -->
          <div class="flex justify-center">
            <img
              src="../../assets/image/creator-2-DrvLH35G.png"
              alt="Multi-platform creator dashboard"
              class="w-full max-w-[350px] h-auto rounded-3xl shadow-2xl animate-float"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- PLATFORMS SECTION -->
    <section class="py-8 bg-gradient-to-b from-grayLight to-background" id="platforms">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="text-center mb-12 space-y-4">
          <div class="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-2">
            COMPATIBILITY
          </div>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Multi-Platform Compatibility
          </h2>
          <p class="text-muted-foreground max-w-2xl mx-auto">
            Our software integrates seamlessly with your existing systems for optimal performance across platforms.
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-12 max-w-4xl mx-auto">
          <div class="bg-card rounded-2xl p-8 shadow-card hover:shadow-xl transition-all border border-border">
            <img src="../../assets/icon/onlyfans-logo-BL9qktiZ.png" alt="OnlyFans" class="h-12 w-auto" />
          </div>
          <div class="bg-card rounded-2xl p-8 shadow-card hover:shadow-xl transition-all border border-border">
            <div class="text-4xl font-bold text-foreground">MANYVIDS</div>
          </div>
          <div class="bg-card rounded-2xl p-8 shadow-card hover:shadow-xl transition-all border border-border">
            <img src="../../assets/icon/fansly-logo-CJB8oDqz.png" alt="Fansly" class="h-12 w-auto" />
          </div>
        </div>
      </div>
    </section>

    <!-- GLOBAL REACH SECTION -->
    <section class="py-8 bg-background" id="global-reach">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="text-center mb-16 space-y-4">
          <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
            Creators Worldwide
          </div>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Powering Growth for Creators<br />Around the Globe
          </h2>
        </div>

        <!-- Country Cards Container -->
        <div class="relative max-w-6xl mx-auto h-[400px] md:h-[500px] rounded-3xl overflow-hidden">
          <svg viewBox="0 0 1200 600" class="w-full h-full" preserveAspectRatio="xMidYMid meet">
            <defs>
              <pattern id="dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="5" cy="5" r="1.5" fill="#d1d5db" opacity="0.4"></circle>
              </pattern>
            </defs>
            <path d="M 150,150 Q 200,120 280,140 L 320,180 Q 300,220 250,240 L 200,220 Q 160,200 150,150 Z" fill="url(#dots)"></path>
            <path d="M 280,320 Q 300,340 290,400 L 270,460 Q 250,440 260,380 L 280,320 Z" fill="url(#dots)"></path>
            <path d="M 520,140 Q 560,130 600,150 L 620,180 Q 600,200 560,190 L 520,170 Z" fill="url(#dots)"></path>
            <path d="M 540,280 Q 580,270 620,290 L 640,380 Q 620,440 580,450 L 540,420 Q 520,360 540,280 Z" fill="url(#dots)"></path>
            <path d="M 680,140 Q 820,120 940,160 L 1000,200 Q 980,280 900,300 L 800,280 Q 700,240 680,180 Z" fill="url(#dots)"></path>
            <path d="M 900,420 Q 960,410 1000,440 L 980,480 Q 940,490 900,470 Z" fill="url(#dots)"></path>
          </svg>

          <!-- USA -->
          <div class="absolute top-[30%] left-[15%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-background/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-border">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">US</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">USA</p>
                <p class="text-[8px] md:text-xs text-green-600 font-medium leading-tight">↑ 6.2%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- Brazil -->
          <div class="absolute top-[62%] left-[22%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">BR</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">Brazil</p>
                <p class="text-[8px] md:text-xs text-red-500 font-medium leading-tight">↓ 4.1%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- England -->
          <div class="absolute top-[22%] left-[43%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">GB</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">England</p>
                <p class="text-[8px] md:text-xs text-green-600 font-medium leading-tight">↑ 8.3%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- Austria -->
          <div class="absolute top-[35%] left-[51%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">AT</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">Austria</p>
                <p class="text-[8px] md:text-xs text-green-600 font-medium leading-tight">↑ 2.50%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- South Africa -->
          <div class="absolute top-[72%] left-[48%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">ZA</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">South Africa</p>
                <p class="text-[8px] md:text-xs text-red-500 font-medium leading-tight">↓ 5.43%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- India -->
          <div class="absolute top-[50%] left-[67%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">IN</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">India</p>
                <p class="text-[8px] md:text-xs text-red-500 font-medium leading-tight">↓ 1.3%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- China -->
          <div class="absolute top-[32%] left-[76%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">CN</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">China</p>
                <p class="text-[8px] md:text-xs text-green-600 font-medium leading-tight">↑ 1.14%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- Indonesia -->
          <div class="absolute top-[65%] left-[82%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">ID</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">Indonesia</p>
                <p class="text-[8px] md:text-xs text-red-500 font-medium leading-tight">↓ 4.8%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>

          <!-- Japan -->
          <div class="absolute top-[20%] left-[88%] transform -translate-x-1/2 -translate-y-1/2">
            <div class="flex items-center gap-1.5 md:gap-2 bg-white/95 backdrop-blur-sm px-1.5 md:px-3 py-1 md:py-2 rounded-xl md:rounded-2xl shadow-lg border border-gray-300">
              <div class="w-5 h-5 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span class="text-[8px] md:text-xs font-bold text-primary">JP</span>
              </div>
              <div>
                <p class="font-semibold text-foreground text-[9px] md:text-sm whitespace-nowrap leading-tight">Japan</p>
                <p class="text-[8px] md:text-xs text-green-600 font-medium leading-tight">↑ 3.40%</p>
              </div>
            </div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full animate-pulse -z-10"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT SECTION -->
    <section class="py-8 bg-grayLight" id="contact">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-12 space-y-4">
            <div class="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-2">
              GET IN TOUCH
            </div>
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Ready to Scale Your<br />Creator Business?
            </h2>
            <p class="text-muted-foreground max-w-xl mx-auto mb-4">
              Ready to transform your creator business? Start your 2-week free trial today — no credit card required.
            </p>
            <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
              All 10 Modules for - $149 per month
            </div>
            <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
              2-Week FREE TRIAL • No Credit Card
            </div>
          </div>
          <div class="grid md:grid-cols-2 gap-6 md:gap-8">
            <div class="space-y-6">
              <div class="bg-card rounded-2xl p-6 shadow-card border border-border">
                <div class="flex items-center gap-3 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-primary">
                    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                  </svg>
                  <h3 class="font-semibold text-foreground">Support</h3>
                </div>
                <p class="text-sm text-muted-foreground mb-2">Need help? We're here 24/7</p>
                <a href="mailto:admin@optimaaicreator.com" class="text-primary hover:underline font-medium">
                  admin@optimaaicreator.com
                </a>
              </div>
              <div class="bg-card rounded-2xl p-6 shadow-card border border-border">
                <h3 class="font-semibold text-foreground mb-3">Quick Response</h3>
                <p class="text-sm text-muted-foreground">
                  We typically reply within 24 hours. Most creators hear back in under 4 hours.
                </p>
              </div>
            </div>
            <div class="bg-card rounded-2xl p-8 shadow-card border border-border">
              <form @submit.prevent="handleSubmit" class="space-y-4">
                <div>
                  <label for="name" class="text-sm font-medium text-foreground mb-2 block">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    v-model="formData.name"
                    placeholder="Your name"
                    class="flex h-10 w-full rounded-md border border-border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-background"
                    required
                  />
                </div>
                <div>
                  <label for="email" class="text-sm font-medium text-foreground mb-2 block">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    v-model="formData.email"
                    placeholder="your@email.com"
                    class="flex h-10 w-full rounded-md border border-border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-background"
                    required
                  />
                </div>
                <div>
                  <label for="message" class="text-sm font-medium text-foreground mb-2 block">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    v-model="formData.message"
                    placeholder="Type your message..."
                    class="flex w-full rounded-md border border-border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 bg-background min-h-[120px]"
                    required
                  />
                </div>
                <button
                  type="submit"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2 w-full bg-primary hover:bg-primary/90 text-base font-semibold"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                  </svg>
                  Start Your Free Trial
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="py-16 border-t border-gray-200 bg-white">
      <div class="container mx-auto max-w-[1200px] px-6">
        <div class="grid md:grid-cols-4 gap-8 mb-12">
          <div class="md:col-span-2 space-y-4">
            <div class="flex items-center gap-2">
              <img src="../../assets/icon/logo-CAqQhPFA.png" alt="OptimaAI Creator" class="w-8 h-8" />
              <span class="text-xl font-bold text-gray-900">OptimaAI Creator</span>
            </div>
            <p class="text-gray-600 text-sm max-w-xs">
              AI-powered platform for Only Fans content creators.
            </p>
            <p class="text-gray-600 text-sm">
              Email: admin@optimaaicreator.com
            </p>
          </div>
          <div>
            <h4 class="font-semibold mb-4 text-gray-900">Company</h4>
            <ul class="space-y-2">
              <li>
                <a href="/about-us" target="_blank" rel="noopener noreferrer" class="text-gray-600 text-sm hover:text-[hsl(207,90%,54%)] transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="#contact" class="text-gray-600 text-sm hover:text-[hsl(207,90%,54%)] transition-colors">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 class="font-semibold mb-4 text-gray-900">Legal</h4>
            <ul class="space-y-2">
              <li>
                <a href="/legals" target="_blank" rel="noopener noreferrer" class="text-gray-600 text-sm hover:text-[hsl(207,90%,54%)] transition-colors">
                  Legal
                </a>
              </li>
              <li>
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" class="text-gray-600 text-sm hover:text-[hsl(207,90%,54%)] transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" class="text-gray-600 text-sm hover:text-[hsl(207,90%,54%)] transition-colors">
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div class="pt-8 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
          <p class="text-sm text-gray-600">
            © 2024 OptimaAI Creator. All rights reserved.
          </p>
          <div class="flex items-center gap-4">
            <a
              href="#"
              class="w-10 h-10 rounded-full bg-white border border-gray-200 hover:border-[hsl(207,90%,54%)] flex items-center justify-center transition-colors hover:bg-[hsl(207,90%,54%)]/10"
              aria-label="Twitter"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-gray-900">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
              </svg>
            </a>
            <a
              href="#"
              class="w-10 h-10 rounded-full bg-white border border-gray-200 hover:border-[hsl(207,90%,54%)] flex items-center justify-center transition-colors hover:bg-[hsl(207,90%,54%)]/10"
              aria-label="Instagram"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-gray-900">
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
              </svg>
            </a>
            <a
              href="#"
              class="w-10 h-10 rounded-full bg-white border border-gray-200 hover:border-[hsl(207,90%,54%)] flex items-center justify-center transition-colors hover:bg-[hsl(207,90%,54%)]/10"
              aria-label="LinkedIn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-gray-900">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                <rect width="4" height="12" x="2" y="9"></rect>
                <circle cx="4" cy="4" r="2"></circle>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
    <!-- Video Modal -->
    <div
      v-if="videoModalOpen"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in"
      @click="closeVideoModal"
    >
      <div 
        class="relative w-full max-w-5xl bg-black rounded-2xl overflow-hidden shadow-2xl aspect-video"
        @click.stop
      >
        <button
          @click="closeVideoModal"
          class="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
          aria-label="Close video"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <video
          v-if="currentVideoSrc"
          :src="currentVideoSrc"
          class="w-full h-full object-contain"
          controls
          autoplay
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import promoVideo from '../assets/videos/promo-script-without-logo.mp4';
import helpVideo from '../assets/videos/the-amazing-help-button.mp4';

export default {
  name: "LandingPage",
  mounted() {
    // Prevent body scroll when mobile menu is open
    this.updateBodyScroll();
  },
  watch: {
    mobileMenuOpen() {
      this.updateBodyScroll();
    }
  },
  beforeUnmount() {
    // Restore body scroll when component is destroyed
    document.body.style.overflow = 'unset';
  },
  methods: {
    goToSignup() {
      this.$router.push('/signup');
    },
    /**
     * Handle Watch Video button click
     */
    /**
     * Handle Watch Video button click
     * @param {string} type - 'promo' or 'help'
     */
    watchVideo(type) {
      if (type === 'promo') {
        this.currentVideoSrc = promoVideo;
      } else if (type === 'help') {
        this.currentVideoSrc = helpVideo;
      }
      this.videoModalOpen = true;
      this.updateBodyScroll();
    },
    /**
     * Close video modal
     */
    closeVideoModal() {
      this.videoModalOpen = false;
      this.currentVideoSrc = '';
      this.updateBodyScroll();
    },
    /**
     * Handle Login/Join button click
     */
    handleLoginJoin() {
      this.$router.push('/login');
      this.closeMobileMenu();
    },
    /**
     * Toggle mobile menu open/close
     */
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    /**
     * Close mobile menu
     */
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    /**
     * Update body scroll based on mobile menu state
     */
    updateBodyScroll() {
      if (this.mobileMenuOpen || this.videoModalOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    },
    /**
     * Handle form submission
     */
    handleSubmit() {
      // Handle form submission here
      console.log('Form submitted:', this.formData);
      // TODO: Implement actual form submission logic
      // You can add API call here or redirect to signup
      // For now, redirecting to signup page
      this.$router.push('/signup');
    }
  },
  data() {
    return {
      mobileMenuOpen: false,
      videoModalOpen: false,
      currentVideoSrc: '',
      formData: {
        name: '',
        email: '',
        message: ''
      }
    };
  },
};
</script>

<style scoped>
/* Fade in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

/* Float animation */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
